import { Component, inject, OnInit, ChangeDetectionStrategy } from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import { finalize, firstValueFrom, Observable } from 'rxjs';
import { TransactionRequest } from '../../model/TransactionRequest';
import { UserService } from '../../services/user/user.service';
import {
  TransactionRequestService,
  DecisionContext,
} from '../../services/transaction-request/transaction-request.service';
import {
  ConfirmationDialogComponent,
  ConfirmationDialogData,
} from '../../components/confirmation-dialog/confirmation-dialog.component';
import { User, UserStatus } from 'src/app/model/User';
import { CustomerService } from 'src/app/services/customer/customer.service';
import { FileService } from 'src/app/services/file/file.service';
import { DialogService } from 'src/app/services/dialog/dialog.service';

/** Data provided to the review request dialog */
export interface ReviewRequestDialogData {
  transactionRequest: TransactionRequest;
}

/** Data returned from the review request dialog */
export interface ReviewRequestDialogResult {
  success: boolean;
  action: 'accept' | 'decline' | 'close';
  transactionRequest: TransactionRequest | null;
}

/**
 * Dialog component for reviewing transaction requests
 * Allows viewing request details and approving/denying requests
 */
@Component({
    selector: 'app-review-request-dialog',
    templateUrl: './review-request-dialog.component.html',
    styleUrl: './review-request-dialog.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ReviewRequestDialogComponent implements OnInit {
  /** Dependency injections */
  private readonly _dialogRef: MatDialogRef<ReviewRequestDialogComponent> =
    inject(MatDialogRef);
  private readonly _transactionRequestService: TransactionRequestService =
    inject(TransactionRequestService);
  private readonly _toastrService: ToastrService = inject(ToastrService);
  private readonly _dialog: MatDialog = inject(MatDialog);
  private readonly _customerService: CustomerService = inject(CustomerService);
  private readonly _fileService: FileService = inject(FileService);
  public readonly _userService: UserService = inject(UserService);
  public readonly _dialogService: DialogService = inject(DialogService);

  /** Dialog data */
  data: ReviewRequestDialogData = inject(MAT_DIALOG_DATA);
  user$: Observable<User | null> = this._userService.user$;
  error: string | null = null;
  files: { id: number; name: string; file: File }[] = [];

  /** Status flags */
  isSubmitting: boolean = false;
  loadingCreditCheck: boolean = true;
  loadingInvoices: boolean = true;

  /**
   * On init, load borrower credit information
   */
  async ngOnInit(): Promise<void> {
    // Reset state
    this.loadingCreditCheck = true;
    this.loadingInvoices = true;

    this.borrowerCreditCheck();
    this.loadTransactionFiles();

    // Check for supplier suspension
    if (this.data.transactionRequest.supplier_status === 'suspended') {
      this.error =
        'Supplier is unable to accept transactions. Please contact supplier.';
    }

    // Check for borrower suspension
    const companyStatus = this._userService.company?.companyStatus;
    if (companyStatus === 'suspended') {
      this.error =
        'Your account is currently suspended. You cannot accept transaction requests at this time.';
    }
  }

  /**
   * Helper method to perform a credit check on the borrower
   */
  private async borrowerCreditCheck(): Promise<void> {
    try {
      // Get company object
      const company = this._userService.company;
      if (!company) {
        throw new Error('No company found for user');
      }

      // Get borrower company id
      const companyId = company.companyId;
      if (!companyId) {
        throw new Error('No company ID found');
      }

      // Get credit summary
      const summary = await firstValueFrom(
        this._customerService.getCustomerCreditSummary$(companyId)
      );
      if (!summary) {
        throw new Error('No credit summary found');
      }

      if (this.data.transactionRequest.amount > summary.remainingCredit) {
        this.error =
          'This transaction request exceeds your remaining credit limit';
      }

      this.loadingCreditCheck = false;
    } catch (err) {
      console.error(
        '[REVIEW-REQUEST-DIALOG]: Error loading borrower credit info:',
        err
      );
      this._toastrService.error('Error loading borrower credit info');
    }
  }

  /**
   * Helper method to load transaction files
   */
  private async loadTransactionFiles(): Promise<void> {
    // Load transaction files
    const fileIds = this.data.transactionRequest.fileIds || [];
    if (fileIds.length === 0) {
      this.loadingInvoices = false;
      return;
    }

    // Download all files
    const downloadPromises = fileIds.map((fileId) =>
      firstValueFrom(this._fileService.downloadFile('' + fileId))
    );

    try {
      const downloadedFiles = await Promise.all(downloadPromises);

      this.files = downloadedFiles.map((file, index) => ({
        id: fileIds[index],
        name: file.name,
        file: file,
      }));

      this.loadingInvoices = false;
    } catch (error) {
      console.error('[REVIEW-REQUEST-DIALOG]: Error downloading files:', error);
      this._toastrService.error('Error downloading files');
    }
  }

  /**
   * Open a file in a new tab locally
   *
   * @param index Index of the file to open from this.files array
   */
  public onOpenFile(index: number): void {
    const fileEntry = this.files[index];
    this._fileService.openFile(fileEntry.file);
  }

  /**
   * Open the transaction request offer (TAA) dialog.
   */
  public onTermsClick(): void {
    const transactionRequestId = this.data.transactionRequest.tx_request_id ?? '';
    if (!transactionRequestId) {
      this._toastrService.error('Cannot open terms: missing transaction identifier');
      return;
    }
    this._dialogService.openTransactionRequestOfferDialog({ transactionRequestId });
  }

  /**
   * Handle the accept request flow
   */
  public async onAcceptClick(): Promise<void> {
    if (!this.data.transactionRequest) {
      console.error(
        '[REVIEW-REQUEST-DIALOG]: No transaction request selected to accept'
      );
      this._toastrService.error('Transaction request missing');
      return;
    }

    // Show final confirmation
    const confirmData: ConfirmationDialogData = {
      title: 'Accept Transaction Request',
      message: 'Are you sure you want to accept this transaction request?',
    };

    const obs = this._dialogService.openConfirmationDialog(confirmData);
    const confirmed: boolean = await firstValueFrom(obs);
    if (!confirmed) {
      return;
    }

    this.isSubmitting = true;

    const ctx = await this.buildDecisionContext();

    // Accept the request
    this._transactionRequestService
      .handleRequest(this.data.transactionRequest.id, 'accepted', ctx)
      .pipe(finalize(() => (this.isSubmitting = false)))
      .subscribe({
        next: (updatedRequest: TransactionRequest) => {
          this._toastrService.success(
            `Transaction request accepted successfully`
          );
          this._dialogRef.close({
            success: true,
            action: 'accept',
            transactionRequest: updatedRequest,
          });
        },
        error: (error) => {
          console.error('Error accepting transaction request:', error);
          this._toastrService.error('Failed to accept transaction request');
        },
      });
  }

  /**
   * Handle the decline request flow
   */
  public async onDeclineClick(): Promise<void> {
    if (!this.data.transactionRequest) {
      console.error(
        '[REVIEW-REQUEST-DIALOG]: No transaction request selected to decline'
      );
      return;
    }

    // Show confirmation dialog
    const dialogData: ConfirmationDialogData = {
      title: 'Decline Transaction Request',
      message: 'Are you sure you want to decline this transaction request?',
    };

    const obs = this._dialogService.openConfirmationDialog(dialogData);
    const confirmed = await firstValueFrom(obs);
    if (!confirmed) {
      return;
    }

    this.isSubmitting = true;
    const ctx = await this.buildDecisionContext();

    // Decline the request

    this._transactionRequestService
      .handleRequest(this.data.transactionRequest.id, 'declined', ctx)
      .pipe(finalize(() => (this.isSubmitting = false)))
      .subscribe({
        next: (updatedRequest: TransactionRequest) => {
          this._toastrService.success(
            `Transaction request declined successfully`
          );
          this._dialogRef.close({
            success: true,
            action: 'decline',
            transactionRequest: updatedRequest,
          });
        },
        error: (error) => {
          console.error('Error declining transaction request:', error);
          this._toastrService.error('Failed to decline transaction request');
        },
      });
  }

  /**
   * Close the dialog without saving
   */
  public cancel(): void {
    this._dialogRef.close({
      success: false,
      action: 'close',
      transactionRequest: null,
    });
  }

  private async buildDecisionContext(): Promise<DecisionContext> {
    const navAny = navigator as any;

    const location = await this.getBrowserLocation(); // may prompt user

    return {
      userAgent: navigator.userAgent,
      platform: navAny.userAgentData?.platform ?? navigator.platform ?? null,
      decidedAt: new Date().toISOString(),
      location, // ✅ include
    };
  }

  private async getBrowserLocation(): Promise<{
    lat: number;
    lon: number;
  } | null> {
    if (!navigator.geolocation) return null;

    return new Promise((resolve) => {
      navigator.geolocation.getCurrentPosition(
        (pos) =>
          resolve({ lat: pos.coords.latitude, lon: pos.coords.longitude }),
        () => resolve(null),
        { enableHighAccuracy: false, timeout: 5000 }
      );
    });
  }
}
